import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PresentationChart, ChartLineUp, ClipboardText } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import ValuationCalculator from "@/components/ValuationCalculator";

/* Clinq brand tokens (matched to clinq.africa) */
const C = {
  bgDark: "#0A2826",       // deep forest-teal
  bgDeep: "#072120",
  bgPanel: "#103532",       // panel/section break
  teal: "#2CC5A8",          // primary accent / "secure capital" green
  tealSoft: "#1F8F7C",      // hover/contrast
  cream: "#F7F2EA",         // light section base
  creamSoft: "#FAF6F1",
  dot: "#F39C12",           // orange logo-dot accent
  orange: "#E67E22",        // deep orange accent (FM card, eyebrows)
  ink: "#0A2826",
};

/* --- Brand logo: matches clinq.africa wordmark exactly (Manrope + orange dot above dotless ı) + Funder Readiness lockup --- */
const ClinqLogo = ({ light = true, withTagline = true, size = "md" }) => {
  const sz = size === "lg" ? "text-3xl" : size === "sm" ? "text-xl" : "text-2xl";
  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <span
        className={`relative inline-flex items-baseline font-extrabold tracking-tight ${sz}`}
        style={{
          fontFamily: "Manrope, sans-serif",
          letterSpacing: "-0.03em",
          color: "#0A8B85",
        }}
      >
        Cl
        <span className="relative inline-block">
          ı
          <span
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
            style={{ backgroundColor: "#F59E0B", height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}
          ></span>
        </span>
        nq
      </span>
      {withTagline && (
        <div className={`hidden sm:flex flex-col leading-none border-l pl-3 ${light ? "border-white/15" : "border-stone-300"}`}>
          <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#7BD3CE" }}>Funder</span>
          <span className={`text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 ${light ? "text-white/55" : "text-stone-500"}`}>Readiness</span>
        </div>
      )}
    </div>
  );
};

/* --- Inline SVG diagrams: richer, document-style illustrations per service --- */
const PitchDiagram = () => (
  <svg viewBox="0 0 200 140" className="w-full h-auto" aria-hidden="true">
    {/* Back slides peeking through */}
    <rect x="14" y="30" width="125" height="92" rx="5" fill={C.cream} stroke={C.teal} strokeWidth="1.2" opacity="0.55" />
    <rect x="28" y="20" width="125" height="92" rx="5" fill="#E5F7F2" stroke={C.teal} strokeWidth="1.2" opacity="0.85" />

    {/* Front slide */}
    <rect x="42" y="10" width="138" height="100" rx="5" fill="#fff" stroke={C.bgDark} strokeWidth="2" />

    {/* Slide title */}
    <rect x="52" y="20" width="62" height="6" rx="2" fill={C.bgDark} />
    <rect x="52" y="30" width="42" height="3" rx="1.5" fill={C.teal} opacity="0.6" />

    {/* Left content: chart preview */}
    <rect x="52" y="40" width="58" height="34" rx="3" fill="#F8F4EC" stroke={C.teal} strokeWidth="0.8" />
    <path d="M56 68 L66 60 L76 64 L86 52 L96 56 L106 44" stroke={C.teal} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    <circle cx="106" cy="44" r="2" fill={C.dot} />
    <line x1="56" y1="70" x2="106" y2="70" stroke={C.bgDark} strokeWidth="0.5" opacity="0.3" />

    {/* Right content: bullets */}
    <circle cx="118" cy="46" r="1.5" fill={C.dot} />
    <rect x="123" y="44.5" width="48" height="3" rx="1" fill={C.bgDark} opacity="0.75" />
    <circle cx="118" cy="54" r="1.5" fill={C.dot} />
    <rect x="123" y="52.5" width="42" height="3" rx="1" fill={C.bgDark} opacity="0.75" />
    <circle cx="118" cy="62" r="1.5" fill={C.dot} />
    <rect x="123" y="60.5" width="50" height="3" rx="1" fill={C.bgDark} opacity="0.75" />
    <circle cx="118" cy="70" r="1.5" fill={C.dot} />
    <rect x="123" y="68.5" width="36" height="3" rx="1" fill={C.bgDark} opacity="0.75" />

    {/* KPI footer tiles */}
    <rect x="52" y="80" width="38" height="22" rx="3" fill={C.bgDark} />
    <text x="55" y="89" fontFamily="monospace" fontSize="4.5" fill={C.teal} opacity="0.7">USERS</text>
    <text x="55" y="98" fontFamily="monospace" fontSize="7" fill="#fff" fontWeight="bold">12.4K</text>

    <rect x="94" y="80" width="38" height="22" rx="3" fill={C.teal} />
    <text x="97" y="89" fontFamily="monospace" fontSize="4.5" fill={C.bgDark} opacity="0.8">GROWTH</text>
    <text x="97" y="98" fontFamily="monospace" fontSize="7" fill={C.bgDark} fontWeight="bold">+118%</text>

    <rect x="136" y="80" width="38" height="22" rx="3" fill="#F8F4EC" stroke={C.teal} strokeWidth="0.8" />
    <text x="139" y="89" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft}>RAISING</text>
    <text x="139" y="98" fontFamily="monospace" fontSize="7" fill={C.bgDark} fontWeight="bold">$1.2M</text>

    {/* Page indicator */}
    <text x="166" y="108" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft}>04 / 12</text>

    {/* Approved checkmark */}
    <circle cx="174" cy="14" r="8" fill={C.dot} />
    <path d="M170 14 l3 3 l5 -6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ModelDiagram = () => (
  <svg viewBox="0 0 200 140" className="w-full h-auto" aria-hidden="true">
    <rect x="10" y="10" width="180" height="120" rx="6" fill="#fff" stroke={C.teal} strokeWidth="2" />

    {/* Top header bar */}
    <path d="M10 16 a6 6 0 0 1 6 -6 h168 a6 6 0 0 1 6 6 v8 h-180 z" fill={C.bgDark} />
    <text x="16" y="20" fontFamily="monospace" fontSize="6" fill={C.teal} fontWeight="bold">5-YEAR FORECAST</text>
    <circle cx="178" cy="17" r="1.8" fill={C.dot} />
    <circle cx="172" cy="17" r="1.8" fill="#fff" opacity="0.3" />
    <circle cx="166" cy="17" r="1.8" fill="#fff" opacity="0.3" />

    {/* Column headers */}
    <text x="56" y="34" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">Y1</text>
    <text x="80" y="34" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">Y2</text>
    <text x="104" y="34" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">Y3</text>
    <text x="128" y="34" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">Y4</text>
    <text x="152" y="34" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">Y5</text>
    <line x1="14" y1="36" x2="186" y2="36" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />

    {/* Revenue row */}
    <text x="14" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>Revenue</text>
    <text x="54" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>180</text>
    <text x="78" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>420</text>
    <text x="102" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>880</text>
    <text x="126" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>1.6K</text>
    <text x="150" y="45" fontFamily="monospace" fontSize="6" fill={C.bgDark}>2.8K</text>

    {/* Gross row */}
    <text x="14" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>Gross</text>
    <text x="54" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>108</text>
    <text x="78" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>260</text>
    <text x="102" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>560</text>
    <text x="126" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>1.0K</text>
    <text x="150" y="55" fontFamily="monospace" fontSize="6" fill={C.bgDark}>1.9K</text>

    {/* EBITDA highlighted row */}
    <rect x="12" y="59" width="176" height="11" fill="#E5F7F2" />
    <text x="14" y="67" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">EBITDA</text>
    <text x="54" y="67" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">(40)</text>
    <text x="78" y="67" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">35</text>
    <text x="102" y="67" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">180</text>
    <text x="126" y="67" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">420</text>
    <text x="150" y="67" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">840</text>

    {/* Chart area */}
    <rect x="14" y="76" width="138" height="48" rx="3" fill="#F9FBFA" stroke={C.teal} strokeWidth="0.5" opacity="0.6" />

    {/* Grid lines */}
    <line x1="18" y1="120" x2="148" y2="120" stroke={C.bgDark} strokeWidth="0.3" opacity="0.2" />
    <line x1="18" y1="108" x2="148" y2="108" stroke={C.bgDark} strokeWidth="0.3" opacity="0.15" />
    <line x1="18" y1="96" x2="148" y2="96" stroke={C.bgDark} strokeWidth="0.3" opacity="0.15" />
    <line x1="18" y1="84" x2="148" y2="84" stroke={C.bgDark} strokeWidth="0.3" opacity="0.15" />

    {/* Bars */}
    <rect x="28" y="112" width="14" height="8" fill="#CFEDE4" rx="1" />
    <rect x="50" y="104" width="14" height="16" fill="#A6E0CF" rx="1" />
    <rect x="72" y="96" width="14" height="24" fill="#6FCFB6" rx="1" />
    <rect x="94" y="87" width="14" height="33" fill={C.teal} rx="1" />
    <rect x="116" y="82" width="14" height="38" fill={C.tealSoft} rx="1" />

    {/* Growth curve overlay */}
    <path d="M35 110 L57 100 L79 90 L101 82 L123 78" stroke={C.dot} strokeWidth="2" fill="none" strokeLinecap="round" />
    <circle cx="35" cy="110" r="1.8" fill={C.dot} />
    <circle cx="57" cy="100" r="1.8" fill={C.dot} />
    <circle cx="79" cy="90" r="1.8" fill={C.dot} />
    <circle cx="101" cy="82" r="1.8" fill={C.dot} />
    <circle cx="123" cy="78" r="2.5" fill={C.dot} stroke="#fff" strokeWidth="0.8" />

    {/* End-state callout */}
    <rect x="156" y="78" width="30" height="46" rx="3" fill={C.bgDark} />
    <text x="159" y="86" fontFamily="monospace" fontSize="4.5" fill={C.teal}>EBITDA</text>
    <text x="159" y="96" fontFamily="monospace" fontSize="8" fill="#fff" fontWeight="bold">$840K</text>
    <text x="159" y="104" fontFamily="monospace" fontSize="4" fill="#fff" opacity="0.5">5Y FORECAST</text>
    <line x1="159" y1="107" x2="183" y2="107" stroke={C.teal} strokeWidth="0.4" opacity="0.5" />
    <text x="159" y="116" fontFamily="monospace" fontSize="4.5" fill={C.dot} fontWeight="bold">↑ +2,100%</text>
    <text x="159" y="121" fontFamily="monospace" fontSize="3.5" fill="#fff" opacity="0.5">vs Year 1</text>
  </svg>
);

const AccountsDiagram = () => (
  <svg viewBox="0 0 200 140" className="w-full h-auto" aria-hidden="true">
    <rect x="10" y="10" width="180" height="120" rx="6" fill="#fff" stroke={C.teal} strokeWidth="2" />

    {/* Header */}
    <path d="M10 16 a6 6 0 0 1 6 -6 h168 a6 6 0 0 1 6 6 v8 h-180 z" fill={C.bgDark} />
    <text x="16" y="20" fontFamily="monospace" fontSize="6" fill={C.teal} fontWeight="bold">MAR 2026 · BOARD PACK</text>
    <circle cx="178" cy="17" r="1.8" fill={C.dot} />

    {/* KPI tiles */}
    <rect x="14" y="28" width="55" height="22" rx="3" fill="#E5F7F2" />
    <text x="18" y="36" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">REVENUE</text>
    <text x="18" y="46" fontFamily="monospace" fontSize="9" fill={C.bgDark} fontWeight="bold">$4.2M</text>
    <text x="51" y="46" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">↑18%</text>

    <rect x="72" y="28" width="55" height="22" rx="3" fill="#CFEDE4" />
    <text x="76" y="36" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">EBITDA</text>
    <text x="76" y="46" fontFamily="monospace" fontSize="9" fill={C.bgDark} fontWeight="bold">22%</text>
    <text x="105" y="46" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">↑3pp</text>

    <rect x="130" y="28" width="55" height="22" rx="3" fill="#A6E0CF" />
    <text x="134" y="36" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">CASH</text>
    <text x="134" y="46" fontFamily="monospace" fontSize="9" fill={C.bgDark} fontWeight="bold">$890K</text>

    {/* Section divider */}
    <text x="14" y="60" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">P&amp;L · ACTUAL vs BUDGET</text>
    <line x1="14" y1="62" x2="186" y2="62" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />

    {/* Header row */}
    <text x="110" y="69" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft}>ACT</text>
    <text x="138" y="69" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft}>BUD</text>
    <text x="168" y="69" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft}>VAR</text>

    {/* Row 1: Revenue */}
    <text x="14" y="79" fontFamily="monospace" fontSize="6" fill={C.bgDark}>Revenue</text>
    <rect x="55" y="75" width="48" height="4" rx="1" fill="#E5F7F2" />
    <rect x="55" y="75" width="42" height="4" rx="1" fill={C.teal} />
    <text x="108" y="79" fontFamily="monospace" fontSize="6" fill={C.bgDark}>420</text>
    <text x="136" y="79" fontFamily="monospace" fontSize="6" fill={C.bgDark}>400</text>
    <text x="164" y="79" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">+5%</text>

    {/* Row 2: Gross */}
    <text x="14" y="89" fontFamily="monospace" fontSize="6" fill={C.bgDark}>Gross %</text>
    <rect x="55" y="85" width="48" height="4" rx="1" fill="#E5F7F2" />
    <rect x="55" y="85" width="36" height="4" rx="1" fill={C.teal} />
    <text x="108" y="89" fontFamily="monospace" fontSize="6" fill={C.bgDark}>68%</text>
    <text x="136" y="89" fontFamily="monospace" fontSize="6" fill={C.bgDark}>65%</text>
    <text x="164" y="89" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">+3pp</text>

    {/* Row 3: OpEx (over budget) */}
    <text x="14" y="99" fontFamily="monospace" fontSize="6" fill={C.bgDark}>OpEx</text>
    <rect x="55" y="95" width="48" height="4" rx="1" fill="#E5F7F2" />
    <rect x="55" y="95" width="40" height="4" rx="1" fill={C.dot} />
    <text x="108" y="99" fontFamily="monospace" fontSize="6" fill={C.bgDark}>240</text>
    <text x="136" y="99" fontFamily="monospace" fontSize="6" fill={C.bgDark}>220</text>
    <text x="164" y="99" fontFamily="monospace" fontSize="6" fill={C.dot} fontWeight="bold">-9%</text>

    {/* EBITDA highlighted row */}
    <rect x="12" y="103" width="176" height="12" fill="#E5F7F2" />
    <text x="14" y="111" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">EBITDA</text>
    <rect x="55" y="107" width="48" height="4" rx="1" fill="#fff" />
    <rect x="55" y="107" width="38" height="4" rx="1" fill={C.bgDark} />
    <text x="108" y="111" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">94</text>
    <text x="136" y="111" fontFamily="monospace" fontSize="6" fill={C.bgDark} fontWeight="bold">75</text>
    <text x="164" y="111" fontFamily="monospace" fontSize="6" fill={C.tealSoft} fontWeight="bold">+25%</text>

    {/* Footer status */}
    <text x="14" y="124" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">✓ RECONCILED</text>
    <text x="146" y="124" fontFamily="monospace" fontSize="4.5" fill={C.tealSoft} fontWeight="bold">BOARD-READY</text>
  </svg>
);

const SERVICES = [
  {
    id: "pitch",
    name: "Pitch Deck",
    tagline: "Tell the story funders back.",
    status: "live",
    Diagram: PitchDiagram,
    why: "Your pitch deck is your first impression. Funders decide in minutes whether your business is worth a second meeting — or a hard no.",
    investorsLookFor: [
      "A real problem people pay to solve",
      "A clear solution and a big enough market",
      "A founder who knows the problem inside out",
      "Traction, and a smart use of the money",
    ],
    story: "Why this problem, why you, why now.",
  },
  {
    id: "model",
    name: "Financial Model",
    tagline: "3 + 5 years, bespoke to your business.",
    status: "live",
    Diagram: ModelDiagram,
    why: "A good model turns your story into numbers funders can stress-test. It's where due diligence either picks up speed — or falls apart.",
    investorsLookFor: [
      "Assumptions that fit your stage and sector",
      "Revenue built bottom-up, not a hockey stick",
      "P&L, balance sheet and cash flow that tie",
      "A clear ask, runway and break-even point",
    ],
    story: "How every dollar moves through the business, and when it comes back.",
  },
  {
    id: "accounts",
    name: "Management Accounts",
    tagline: "Board-ready, every month.",
    status: "live",
    Diagram: AccountsDiagram,
    why: "Management accounts prove the business you pitched is the one you actually run. They earn trust — and unlock the next tranche.",
    investorsLookFor: [
      "Monthly numbers, on time, in the same format",
      "Actuals compared to budget and the model",
      "Clean revenue, costs and cash position",
      "KPIs that show real progress, not just spend",
    ],
    story: "What happened last month, why, and what you're doing about it.",
  },
];

const Pill = ({ children, light = false }) => (
  <span className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full border
    ${light
      ? "border-white/20 text-white/90 bg-white/5"
      : "border-stone-300 text-stone-700 bg-white"}`}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.dot }}></span>
    {children}
  </span>
);

/* --- Journey diagram helpers --- */
const JourneyStep = ({ num, kicker, title, deliverable, desc, icon }) => (
  <div className="flex flex-col items-center text-center px-2" data-testid={`journey-step-${num}`}>
    <div className="relative">
      <div className="w-28 h-28 rounded-2xl border-2 grid place-items-center transition-transform hover:-translate-y-1"
        style={{ backgroundColor: C.bgPanel, borderColor: C.teal }}>
        {icon}
      </div>
      <div className="absolute -top-2 -left-2 w-9 h-9 rounded-full grid place-items-center font-mono-num font-bold text-xs shadow-lg"
        style={{ backgroundColor: C.teal, color: C.bgDark }}>
        {num}
      </div>
    </div>
    <div className="mt-5">
      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em]" style={{ color: C.dot }}>{kicker}</div>
      <div className="font-display font-extrabold text-3xl text-white tracking-tight mt-1">{title}</div>
      <div className="text-sm mt-2" style={{ color: C.teal }}>{deliverable}</div>
      <div className="text-xs text-white/50 mt-2 leading-relaxed max-w-[200px] mx-auto">{desc}</div>
    </div>
  </div>
);

const Connector = ({ arrow = false }) => (
  <div className="hidden lg:flex items-center justify-center px-1" aria-hidden="true">
    <div className="relative w-full flex items-center" style={{ marginTop: "-3.5rem" }}>
      <svg viewBox="0 0 80 12" className="w-full h-3 overflow-visible">
        <line x1="2" y1="6" x2={arrow ? "70" : "78"} y2="6"
          stroke={C.teal} strokeWidth="1.5"
          strokeDasharray="4 4" opacity="0.55" />
        {arrow && (
          <>
            <line x1="64" y1="1" x2="74" y2="6" stroke={C.teal} strokeWidth="2" strokeLinecap="round" />
            <line x1="64" y1="11" x2="74" y2="6" stroke={C.teal} strokeWidth="2" strokeLinecap="round" />
          </>
        )}
      </svg>
    </div>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const chipsRef = React.useRef(null);

  React.useEffect(() => {
    const grid = chipsRef.current;
    if (!grid) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stagger the children so they cascade in
            const chips = grid.querySelectorAll(".clinq-chip-rise");
            chips.forEach((el, idx) => {
              el.style.animationDelay = `${idx * 120}ms`;
              el.classList.add("is-visible");
            });
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(grid);
    return () => io.disconnect();
  }, []);

  const handleCTA = (s) => {
    // Financial model + management accounts route straight into the picker page,
    // pre-selecting the correct tile via the ?type= param.
    if (s && (s.id === "model" || s.id === "accounts")) {
      navigate(`/build/start?type=${s.id}`);
      return;
    }
    navigate("/signup");
  };

  return (
    <div className="min-h-screen selection:text-white" style={{ backgroundColor: C.bgDark, color: "#fff", ['--sel' ]: C.teal }}>
      <SEO
        title="Funder-ready pitch decks, financial models & management accounts"
        description="Clinq Prep helps African founders build the three documents every funder asks for — a bespoke pitch deck, financial model and management accounts, tailored to your business, stage and industry."
        path="/"
      />
      <style>{`::selection { background: ${C.teal}; color: #fff; }`}</style>

      {/* Nav — matches clinq.africa: black bg, logo left, Log In + Sign Up right */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo-link">
            <ClinqLogo light withTagline />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login"
              className="text-sm font-medium text-white/90 hover:text-white transition-colors px-3 py-2">
              Log In
            </Link>
            <button onClick={() => navigate("/signup")} data-testid="nav-cta-signup"
              className="text-sm font-semibold px-4 py-2 rounded-md transition-colors"
              style={{ backgroundColor: "#0A8B85", color: "#fff" }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#066667"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#0A8B85"}>
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO — dark, photo right, big teal-accented headline */}
      <section className="relative overflow-hidden" style={{ backgroundColor: C.bgDark }}>
        {/* radial glow */}
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full opacity-20 blur-3xl"
          style={{ background: `radial-gradient(circle, ${C.teal} 0%, transparent 60%)` }} aria-hidden="true"></div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <Pill light>Clinq for Business · Capital Readiness</Pill>
            <h1 className="font-display font-extrabold tracking-tighter text-[2.4rem] sm:text-6xl lg:text-7xl leading-[1.05] sm:leading-[1.02] text-white mt-6 sm:mt-7 break-words">
              Is your business{" "}
              <span style={{ color: C.teal }}>funder&#8209;ready,</span>{" "}
              or <span className="italic text-white/50">just hopeful?</span>
            </h1>
            <p className="mt-6 sm:mt-7 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              Funders are more likely to back your business when you&apos;re fully prepared to demonstrate your scalability, mitigate your risks, and present a business case that is impossible to ignore.
            </p>
            <div className="mt-9 flex items-center gap-5">
              <button onClick={() => navigate("/build/start")} data-testid="hero-cta-build"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base transition-colors"
                style={{ backgroundColor: C.teal, color: C.bgDark }}>
                Get started <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Services — creative 3-up card chips */}
            <div className="mt-12 pt-8 border-t border-white/10" data-testid="hero-services-line">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8" style={{ backgroundColor: C.dot }}></div>
                <span className="text-[10px] font-semibold uppercase tracking-[0.3em]" style={{ color: C.dot }}>
                  Get help preparing a
                </span>
              </div>

              <div ref={chipsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-service-chips">
                {[
                  {
                    id: "pitch",
                    num: "01",
                    name: "Pitch Deck",
                    tag: "Tell the story",
                    status: "live",
                    icon: (
                      <svg viewBox="0 0 40 30" className="w-full h-full">
                        <rect x="3" y="9" width="22" height="17" rx="2" fill="none" stroke={C.teal} strokeWidth="1" opacity="0.5" />
                        <rect x="8" y="6" width="22" height="17" rx="2" fill="none" stroke={C.teal} strokeWidth="1" opacity="0.75" />
                        <rect x="13" y="3" width="24" height="19" rx="2" fill={C.bgPanel} stroke={C.teal} strokeWidth="1.2" />
                        <rect x="16" y="7" width="10" height="2" rx="1" fill={C.teal} />
                        <rect x="16" y="11" width="16" height="1.5" rx="0.5" fill="#fff" opacity="0.4" />
                        <rect x="16" y="14" width="14" height="1.5" rx="0.5" fill="#fff" opacity="0.4" />
                        <rect x="16" y="17" width="12" height="1.5" rx="0.5" fill="#fff" opacity="0.4" />
                      </svg>
                    ),
                  },
                  {
                    id: "model",
                    num: "02",
                    name: "Financial Model",
                    tag: "Prove the maths · ~15 min",
                    status: "live",
                    icon: (
                      <svg viewBox="0 0 40 30" className="w-full h-full">
                        <rect x="3" y="3" width="34" height="24" rx="2" fill={C.bgPanel} stroke={C.teal} strokeWidth="1.2" />
                        <rect x="3" y="3" width="34" height="5" rx="2" fill={C.teal} opacity="0.3" />
                        <line x1="11" y1="3" x2="11" y2="27" stroke={C.teal} strokeWidth="0.4" opacity="0.4" />
                        <line x1="20" y1="3" x2="20" y2="27" stroke={C.teal} strokeWidth="0.4" opacity="0.4" />
                        <line x1="29" y1="3" x2="29" y2="27" stroke={C.teal} strokeWidth="0.4" opacity="0.4" />
                        <line x1="3" y1="13" x2="37" y2="13" stroke={C.teal} strokeWidth="0.4" opacity="0.4" />
                        <line x1="3" y1="20" x2="37" y2="20" stroke={C.teal} strokeWidth="0.4" opacity="0.4" />
                        <path d="M6 23 L13 18 L21 14 L29 10 L35 6" stroke={C.dot} strokeWidth="1.4" fill="none" strokeLinecap="round" />
                        <circle cx="35" cy="6" r="1.5" fill={C.dot} />
                      </svg>
                    ),
                  },
                  {
                    id: "accounts",
                    num: "03",
                    name: "Management Accounts",
                    tag: "Reconcile monthly",
                    status: "live",
                    icon: (
                      <svg viewBox="0 0 40 30" className="w-full h-full">
                        <rect x="6" y="2" width="28" height="26" rx="2" fill={C.bgPanel} stroke={C.teal} strokeWidth="1.2" />
                        <rect x="9" y="5" width="22" height="3" rx="1" fill={C.teal} opacity="0.3" />
                        <rect x="9" y="5" width="11" height="3" rx="1" fill={C.teal} />
                        <rect x="9" y="11" width="22" height="1.5" rx="0.5" fill="#fff" opacity="0.3" />
                        <rect x="9" y="11" width="16" height="1.5" rx="0.5" fill={C.teal} opacity="0.8" />
                        <rect x="9" y="15" width="22" height="1.5" rx="0.5" fill="#fff" opacity="0.3" />
                        <rect x="9" y="15" width="12" height="1.5" rx="0.5" fill={C.teal} opacity="0.8" />
                        <rect x="9" y="19" width="22" height="1.5" rx="0.5" fill="#fff" opacity="0.3" />
                        <rect x="9" y="19" width="8" height="1.5" rx="0.5" fill={C.dot} />
                        <circle cx="28" cy="24" r="2.5" fill={C.teal} />
                        <path d="M26.5 24 l1 1 l2 -2" stroke="#0A2826" strokeWidth="0.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ),
                  },
                  {
                    id: "valuation",
                    num: "04",
                    name: "Business Valuation",
                    tag: "Triangulate the number",
                    status: "live",
                    icon: (
                      <svg viewBox="0 0 40 30" className="w-full h-full">
                        <rect x="3" y="3" width="34" height="24" rx="2" fill={C.bgPanel} stroke={C.dot} strokeWidth="1.2" />
                        <rect x="7" y="7" width="9" height="3" rx="0.6" fill={C.dot} opacity="0.6" />
                        <rect x="7" y="12" width="26" height="1.5" rx="0.5" fill="#fff" opacity="0.35" />
                        <rect x="7" y="16" width="22" height="1.5" rx="0.5" fill="#fff" opacity="0.35" />
                        <rect x="8" y="21" width="6" height="4" rx="0.6" fill={C.teal} opacity="0.7" />
                        <rect x="16" y="20" width="6" height="5" rx="0.6" fill={C.dot} />
                        <rect x="24" y="21" width="6" height="4" rx="0.6" fill={C.teal} opacity="0.7" />
                      </svg>
                    ),
                  },
                ].map((s) => {
                  const live = s.status === "live";
                  return (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/build/start?type=${s.id}`)}
                      data-testid={`hero-chip-${s.id}`}
                      className="clinq-chip-rise group relative text-left rounded-xl p-4 transition-all hover:-translate-y-0.5 overflow-hidden"
                      style={{
                        backgroundColor: live ? "rgba(44, 197, 168, 0.08)" : "rgba(255,255,255,0.025)",
                        border: live ? `1px solid ${C.teal}` : "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      {/* hover glow */}
                      <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-2xl"
                        style={{ backgroundColor: C.teal }}></div>

                      <div className="relative flex items-start justify-between mb-3">
                        <span className="font-mono-num text-[10px] tracking-[0.2em]" style={{ color: live ? C.teal : "rgba(255,255,255,0.35)" }}>
                          / {s.num}
                        </span>
                        {live ? (
                          <span className="text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            style={{ backgroundColor: C.teal, color: C.bgDark }}>
                            <span className="w-1 h-1 rounded-full bg-current animate-pulse"></span>
                            Live
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">
                            Soon
                          </span>
                        )}
                      </div>

                      <div className="relative flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-extrabold text-base leading-tight tracking-tight text-white" style={{ letterSpacing: "-0.02em" }}>{s.name}</div>
                          <div className="text-[11px] mt-1" style={{ color: live ? C.teal : "rgba(255,255,255,0.5)" }}>{s.tag}</div>
                        </div>
                        <div className="w-10 h-8 flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                          {s.icon}
                        </div>
                      </div>

                      {/* bottom arrow indicator */}
                      <div className="relative mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold">
                        <span style={{ color: live ? C.teal : "rgba(255,255,255,0.4)" }}>
                          Start building
                        </span>
                        <ArrowRight size={11} className="transition-transform group-hover:translate-x-1" style={{ color: live ? C.teal : "rgba(255,255,255,0.4)" }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <img
                src="https://plus.unsplash.com/premium_photo-1672997189781-579bab37089c?fm=jpg&q=80&w=900&auto=format&fit=crop"
                alt="Thoughtful African founder looking aside at his laptop"
                className="rounded-2xl object-cover w-full h-[500px] shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 rounded-xl px-5 py-4 shadow-2xl border" style={{ backgroundColor: C.bgPanel, borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: C.teal }}></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-num">Live now</div>
                    <div className="text-sm font-display font-bold text-white">Financial Model Builder</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NO EXPERTISE REQUIRED — reassurance band between hero and services */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: C.bgDark }} data-testid="no-expertise-section">
        <div className="absolute -top-16 -left-20 w-[420px] h-[420px] rounded-full opacity-[0.09] blur-3xl"
          style={{ background: `radial-gradient(circle, ${C.teal} 0%, transparent 60%)` }} aria-hidden="true"></div>
        <div className="max-w-6xl mx-auto px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.28em]"
                style={{ color: C.orange }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
                Built for founders, not finance teams
              </div>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]"
                style={{ color: "#fff", letterSpacing: "-0.02em" }}>
                You don&apos;t need to be an expert.
                <br />
                <span style={{ color: C.teal }}>You just need to know your business.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-white/70">
                We built Clinq so founders can produce funder-grade materials without a CFO, an analyst, or a consultant on retainer.
                If you understand what your business does, where you want to take it, and what you&apos;re trying to build —
                you&apos;re ready. Answer a series of smart, targeted questions and we handle the rest: the modelling,
                the narrative, the layout, the numbers.
              </p>
              <div className="mt-8">
                <button onClick={() => navigate("/build/start")} data-testid="no-expertise-cta"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm transition-all hover:translate-x-1"
                  style={{ backgroundColor: C.teal, color: C.bgDark }}>
                  Start building — no CV required
                  <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-4">
                {[
                  {
                    step: "01",
                    title: "You answer the questions",
                    body: "Stage, industry, ambition, milestones — plain-language prompts. No accounting jargon, no financial modelling required.",
                  },
                  {
                    step: "02",
                    title: "AI does the heavy lifting",
                    body: "We generate the deck, model, and accounts — tailored to your stage and industry, ready to review and refine.",
                  },
                  {
                    step: "03",
                    title: "You review, tweak, export",
                    body: "Edit anything, regenerate any section, then export a funder-ready pitch deck, Excel model, and board pack.",
                  },
                ].map((row) => (
                  <div key={row.step} data-testid={`no-expertise-step-${row.step}`}
                    className="flex gap-4 p-4 rounded-xl border transition-colors"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.02)",
                      borderColor: "rgba(123, 211, 206, 0.14)",
                    }}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg grid place-items-center font-mono-num font-bold text-sm"
                      style={{ backgroundColor: `${C.teal}20`, color: C.teal }}>
                      {row.step}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{row.title}</div>
                      <div className="text-xs leading-relaxed text-white/55 mt-1">{row.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — alternating zig-zag rows: each tool gets a full row with diagram on alternating sides */}
      <section id="services" className="py-24" style={{ backgroundColor: C.creamSoft }}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-16 max-w-3xl">
            <Pill>Why these three</Pill>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mt-5 leading-[1.05]" style={{ color: C.ink }}>
              The three documents every funder{" "}
              <span style={{ color: C.teal }}>actually reads.</span>
            </h2>
            <p className="mt-5 text-lg text-stone-700 leading-relaxed">
              Funders don&apos;t back decks alone, spreadsheets alone or accounts alone. They back the same story told three ways. Here&apos;s what each one does, and what they&apos;re really looking for.
            </p>
          </div>

          <div className="space-y-8" data-testid="services-grid">
            {SERVICES.map((s, idx) => {
              const Diagram = s.Diagram;
              const live = s.status === "live";
              const flipped = idx % 2 === 1; // every other row reverses
              return (
                <div
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="bg-white rounded-3xl overflow-hidden border transition-all hover:shadow-xl"
                  style={{ borderColor: live ? C.teal : "rgba(10,40,38,0.08)" }}>
                  <div className={`grid grid-cols-1 lg:grid-cols-12 ${flipped ? "lg:[direction:rtl]" : ""}`}>
                    {/* Visual tile */}
                    <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col justify-between [direction:ltr]"
                      style={{ backgroundColor: live ? "#E8F8F3" : "#F4EFE7" }}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono-num text-xs uppercase tracking-[0.2em]" style={{ color: C.tealSoft }}>
                          0{idx + 1} / 03
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"
                          style={{
                            backgroundColor: live ? C.teal : "transparent",
                            color: live ? C.bgDark : C.tealSoft,
                            border: live ? "none" : `1px solid ${C.teal}`,
                          }}
                          data-testid={`service-status-${s.id}`}>
                          {live
                            ? <><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.bgDark }}></span> Live now</>
                            : <><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.dot }}></span> Coming soon</>}
                        </span>
                      </div>

                      <div className="my-6">
                        <Diagram />
                      </div>

                      <div>
                        <h3 className="font-display font-extrabold text-4xl tracking-tight" style={{ color: C.ink }}>
                          {s.name}
                        </h3>
                        <p className="mt-2 text-base italic" style={{ color: C.tealSoft }}>
                          &ldquo;{s.story}&rdquo;
                        </p>
                      </div>
                    </div>

                    {/* Content side */}
                    <div className="lg:col-span-7 p-8 lg:p-12 [direction:ltr]">
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: C.tealSoft }}>
                          Why it matters
                        </div>
                        <p className="text-lg leading-relaxed" style={{ color: C.ink }}>
                          {s.why}
                        </p>
                      </div>

                      <div className="mt-8 pt-8 border-t border-stone-200">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-4" style={{ color: C.tealSoft }}>
                          What funders look for
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                          {s.investorsLookFor.map((point, i) => (
                            <li key={i} className="flex items-start gap-3 text-[15px] leading-snug" style={{ color: C.ink }}>
                              <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold font-mono-num"
                                style={{ backgroundColor: C.bgDark, color: C.teal }}>
                                {i + 1}
                              </span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-8">
                        <button
                          onClick={() => handleCTA(s)}
                          data-testid={`service-cta-${s.id}`}
                          className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all hover:translate-x-1"
                          style={{ backgroundColor: C.bgDark, color: "#fff" }}>
                          Start building
                          <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Why founders need these — not just investors */}
          <div className="mt-24 pt-16 border-t" style={{ borderColor: "rgba(10, 40, 38, 0.12)" }} data-testid="why-founders-need-these">
            <div className="max-w-3xl">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: C.orange }}>
                For the founder, not the funder
              </div>
              <h3 className="mt-3 font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight" style={{ color: C.bgDark, letterSpacing: "-0.02em" }}>
                Why founders need these tools <span className="opacity-40">(not just investors)</span>
              </h3>
              <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(10, 40, 38, 0.72)" }}>
                These three tools aren&apos;t paperwork you create to satisfy a funder — they&apos;re what you use to actually run your business well.
                Investors just get to see the output.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[
                {
                  id: "pitch",
                  icon: <PresentationChart size={20} weight="duotone" />,
                  title: "Pitch Deck",
                  accent: C.teal,
                  bullets: [
                    { h: "Forces the hard questions", b: "Answer what problem you solve, who your customer is, and why you'll win — before someone else asks." },
                    { h: "Gives you a mental model", b: "Every decision gets checked against a real story instead of gut feel." },
                    { h: "Sharpens every conversation", b: "Customers, hires, partners — not just investors." },
                  ],
                },
                {
                  id: "model",
                  icon: <ChartLineUp size={20} weight="duotone" />,
                  title: "Financial Model",
                  accent: C.orange,
                  bullets: [
                    { h: "Tests your business math", b: "Real costs, margins and pricing — so you know if the business actually makes money." },
                    { h: "Warns before cash runs out", b: "See runway in advance. Act early instead of scrambling in a crisis." },
                    { h: "Lets you test decisions safely", b: "Hiring, pricing, losing a client — see the impact on paper before you risk real money." },
                  ],
                },
                {
                  id: "accounts",
                  icon: <ClipboardText size={20} weight="duotone" />,
                  title: "Management Accounts",
                  accent: C.dot,
                  bullets: [
                    { h: "Your monthly report card", b: "Know if you made or lost money last month — not at year-end when it's too late to fix." },
                    { h: "Catches problems early", b: "A customer not paying, costs creeping up — while you can still act." },
                    { h: "Builds discipline and trust", b: "Everyone on the team is looking at the same numbers." },
                  ],
                },
              ].map((card) => (
                <div key={card.id} data-testid={`why-founders-card-${card.id}`}
                  className="rounded-2xl p-7 border transition-colors"
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderColor: "rgba(10, 40, 38, 0.08)",
                  }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg grid place-items-center"
                      style={{ backgroundColor: `${card.accent}18`, color: card.accent }}
                      aria-hidden="true">
                      {card.icon}
                    </div>
                    <h4 className="font-extrabold text-xl tracking-tight" style={{ color: C.bgDark, letterSpacing: "-0.01em" }}>
                      {card.title}
                    </h4>
                  </div>
                  <ul className="mt-5 space-y-4">
                    {card.bullets.map((row, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: card.accent }} aria-hidden="true"></span>
                        <div>
                          <div className="text-sm font-bold" style={{ color: C.bgDark }}>{row.h}</div>
                          <div className="text-sm leading-relaxed mt-0.5" style={{ color: "rgba(10, 40, 38, 0.65)" }}>{row.b}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUATION — additional service block, dark contrast between cream services and dark journey */}
      <section id="valuation" className="py-24 relative overflow-hidden" style={{ backgroundColor: C.bgDark }} data-testid="valuation-section">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.12] blur-3xl"
          style={{ background: `radial-gradient(circle, ${C.dot} 0%, transparent 60%)` }} aria-hidden="true"></div>

        <div className="max-w-7xl mx-auto px-8 relative">
          {/* Indicative valuation calculator — lead magnet at the top */}
          <ValuationCalculator onRequest={() => navigate("/build/start?type=valuation")} />

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* LEFT — headline + copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] px-3 py-1.5 rounded-full"
                style={{ backgroundColor: `${C.dot}1f`, border: `1px solid ${C.dot}55`, color: C.dot }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.dot }}></span>
                Additional service
              </div>
              <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mt-5 leading-[1.05] text-white">
                Get a defensible{" "}
                <span style={{ color: C.dot }}>business valuation</span>.
              </h2>
              <p className="mt-5 text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.78)" }}>
                Funders, banks and acquirers won&apos;t take a number on a slide at face value — they want to see how
                you got there. Our analysts prepare an independent valuation report using the same triangulation
                methods Big-4 firms use, so when you walk into a term-sheet negotiation you have evidence on your side,
                not just optimism.
              </p>

              {/* Why it matters list */}
              <div className="mt-10">
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: C.teal }}>
                  Why founders need this before raising
                </div>
                <ul className="space-y-3.5">
                  {[
                    { num: "01", title: "Defend your pre-money in equity rounds", body: "Stops investors anchoring you to a lower number. A documented valuation shifts the burden of proof — they have to disprove it, not you." },
                    { num: "02", title: "Unlock debt and DFI facilities", body: "Most non-bank lenders require an independent valuation before they release a facility, especially when collateral is intangible (IP, contracts, software)." },
                    { num: "03", title: "Pre-empt cap-table disputes", body: "Co-founder splits, ESOP strikes, secondary sales — every one of these gets messy without an agreed valuation date and method on file." },
                    { num: "04", title: "Get acquired on your terms", body: "Strategic buyers will undervalue you by default. Coming in with a triangulated DCF + comparables report changes the conversation from 'how much can we get away with?' to 'how do we close the gap?'." },
                  ].map((row) => (
                    <li key={row.num} className="flex items-start gap-4">
                      <span className="font-mono-num text-xs tracking-[0.2em] flex-shrink-0 mt-1" style={{ color: C.dot }}>
                        /{row.num}
                      </span>
                      <div>
                        <div className="font-display font-bold text-lg text-white">{row.title}</div>
                        <div className="text-[14px] leading-relaxed mt-1" style={{ color: "rgba(255,255,255,0.65)" }}>{row.body}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* RIGHT — visual card with methodology + CTA */}
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="rounded-2xl p-7 border" style={{ backgroundColor: C.bgPanel, borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em]" style={{ color: C.tealSoft }}>
                  Inside a Clinq valuation
                </div>
                <h3 className="font-display font-extrabold text-2xl text-white mt-3 leading-tight tracking-tight">
                  Three methods, triangulated.
                </h3>
                <p className="text-sm leading-relaxed mt-3" style={{ color: "rgba(255,255,255,0.65)" }}>
                  No single method gives the true picture for an African SME. We run all three, sense-check them
                  against each other, and document our assumptions so any funder can audit the work.
                </p>

                <div className="mt-6 space-y-3">
                  {[
                    { code: "DCF", label: "Discounted cash flow", note: "5-year forecast, sector-calibrated discount rate." },
                    { code: "CMA", label: "Comparable multiples", note: "Revenue, EBITDA & user multiples vs. recent African deals." },
                    { code: "PRT", label: "Precedent transactions", note: "Real exits in your sector, geography & stage." },
                  ].map((m) => (
                    <div key={m.code} className="flex items-start gap-3 p-3 rounded-lg"
                      style={{ backgroundColor: "rgba(0,0,0,0.25)", border: "1px solid rgba(123, 211, 206, 0.12)" }}>
                      <span className="font-mono-num text-[10px] font-bold px-2 py-1 rounded flex-shrink-0"
                        style={{ backgroundColor: C.teal, color: "#fff", letterSpacing: "0.1em" }}>
                        {m.code}
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold text-white text-sm">{m.label}</div>
                        <div className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.55)" }}>{m.note}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-white/5">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: C.tealSoft }}>
                      Deliverable
                    </span>
                    <span className="text-[10px] font-mono-num" style={{ color: "rgba(255,255,255,0.4)" }}>10-15 working days</span>
                  </div>
                  <div className="text-white text-sm leading-relaxed">
                    A 20-page valuation report you can share directly with investors, with a one-page summary
                    that lives in the data room.
                  </div>
                </div>

                <button onClick={() => navigate("/build/start?type=valuation")} data-testid="valuation-cta"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md font-extrabold text-sm transition-colors"
                  style={{ backgroundColor: C.dot, color: C.bgDark }}>
                  Request a valuation <ArrowRight size={14} weight="bold" />
                </button>
                <div className="mt-3 text-[11px] text-center" style={{ color: "rgba(255,255,255,0.4)" }}>
                  An advisor will reach out within one business day to scope the work.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY — visual horizontal flow diagram */}
      <section id="how" className="py-24" style={{ backgroundColor: C.bgDeep }}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-16 max-w-3xl">
            <Pill light>How it works</Pill>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-white mt-5 leading-[1.05] tracking-tight">
              Three documents.{" "}
              <span style={{ color: C.teal }}>Three checkpoints.</span>{" "}
              <span className="text-white/55">One funder-ready outcome.</span>
            </h2>
            <p className="mt-5 text-lg text-white/60 leading-relaxed">
              Each document earns you the right to the next conversation. Build them in order and the path opens itself.
            </p>
          </div>

          {/* The journey diagram */}
          <div className="relative" data-testid="journey">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-y-10 lg:gap-y-0 items-stretch">
              {/* STEP 01 — PITCH */}
              <JourneyStep
                num="01"
                kicker="Open the door"
                title="Pitch"
                deliverable="A funder-ready deck"
                desc="Tell your story so well that you earn the next meeting."
                icon={
                  <svg viewBox="0 0 64 64" className="w-12 h-12">
                    <rect x="8" y="18" width="40" height="32" rx="3" fill={C.bgDeep} stroke={C.teal} strokeWidth="1.5" opacity="0.5" />
                    <rect x="13" y="13" width="40" height="32" rx="3" fill={C.bgDeep} stroke={C.teal} strokeWidth="1.5" opacity="0.85" />
                    <rect x="18" y="8" width="40" height="32" rx="3" fill={C.bgPanel} stroke={C.teal} strokeWidth="2" />
                    <rect x="22" y="14" width="18" height="3" rx="1" fill={C.teal} />
                    <rect x="22" y="20" width="28" height="2" rx="1" fill="#fff" opacity="0.3" />
                    <rect x="22" y="25" width="24" height="2" rx="1" fill="#fff" opacity="0.3" />
                    <rect x="22" y="30" width="26" height="2" rx="1" fill="#fff" opacity="0.3" />
                  </svg>
                }
              />

              <Connector />

              {/* STEP 02 — MODEL */}
              <JourneyStep
                num="02"
                kicker="Survive diligence"
                title="Model"
                deliverable="A bespoke 5-year forecast"
                desc="Turn your story into numbers that survive scrutiny."
                icon={
                  <svg viewBox="0 0 64 64" className="w-12 h-12">
                    <rect x="8" y="10" width="48" height="44" rx="3" fill={C.bgPanel} stroke={C.teal} strokeWidth="2" />
                    <rect x="8" y="10" width="48" height="8" rx="3" fill={C.teal} opacity="0.2" />
                    <line x1="8" y1="18" x2="56" y2="18" stroke={C.teal} strokeWidth="0.8" opacity="0.5" />
                    <line x1="20" y1="10" x2="20" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                    <line x1="32" y1="10" x2="32" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                    <line x1="44" y1="10" x2="44" y2="54" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                    <line x1="8" y1="30" x2="56" y2="30" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                    <line x1="8" y1="42" x2="56" y2="42" stroke={C.teal} strokeWidth="0.5" opacity="0.4" />
                    <path d="M14 48 L26 38 L38 32 L50 22" stroke={C.dot} strokeWidth="2" fill="none" strokeLinecap="round" />
                    <circle cx="50" cy="22" r="2.5" fill={C.dot} />
                  </svg>
                }
              />

              <Connector />

              {/* STEP 03 — ACCOUNTS */}
              <JourneyStep
                num="03"
                kicker="Earn the trust"
                title="Accounts"
                deliverable="Monthly board pack"
                desc="Prove the business you pitched is the one you run."
                icon={
                  <svg viewBox="0 0 64 64" className="w-12 h-12">
                    <rect x="10" y="8" width="44" height="48" rx="3" fill={C.bgPanel} stroke={C.teal} strokeWidth="2" />
                    <rect x="14" y="14" width="36" height="6" rx="1.5" fill={C.teal} opacity="0.25" />
                    <rect x="14" y="14" width="18" height="6" rx="1.5" fill={C.teal} />
                    <rect x="14" y="24" width="36" height="3" rx="1" fill="#fff" opacity="0.25" />
                    <rect x="14" y="24" width="28" height="3" rx="1" fill={C.teal} opacity="0.7" />
                    <rect x="14" y="31" width="36" height="3" rx="1" fill="#fff" opacity="0.25" />
                    <rect x="14" y="31" width="22" height="3" rx="1" fill={C.teal} opacity="0.7" />
                    <rect x="14" y="38" width="36" height="3" rx="1" fill="#fff" opacity="0.25" />
                    <rect x="14" y="38" width="14" height="3" rx="1" fill={C.dot} />
                    <circle cx="46" cy="48" r="6" fill={C.teal} />
                    <path d="M43 48 l2 2 l4 -4" stroke={C.bgDark} strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  </svg>
                }
              />

              <Connector arrow />

              {/* END STATE — FUNDED */}
              <div className="flex flex-col items-center text-center px-4">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full grid place-items-center shadow-2xl"
                    style={{ background: `radial-gradient(circle at 30% 30%, ${C.teal}, ${C.tealSoft})` }}>
                    <svg viewBox="0 0 48 48" className="w-12 h-12">
                      <path d="M12 24 l8 8 l16 -18" stroke={C.bgDark} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="absolute -inset-2 rounded-full -z-10 opacity-30 blur-xl" style={{ backgroundColor: C.teal }}></div>
                </div>
                <div className="mt-5">
                  <div className="font-mono-num text-[10px] uppercase tracking-[0.25em]" style={{ color: C.dot }}>End state</div>
                  <div className="font-display font-extrabold text-3xl text-white tracking-tight mt-1">Funded</div>
                  <div className="text-sm mt-2" style={{ color: C.teal }}>Capital wired.</div>
                  <div className="text-xs text-white/50 mt-1">Next round earned.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER — mimics clinq.africa: black bg, 4-col grid, socials, legal */}
      <footer className="text-white" style={{ backgroundColor: "#000" }} data-testid="landing-footer">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 pb-10 pt-16">
          <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
            {/* Logo column */}
            <div className="col-span-2 md:col-span-1">
              <ClinqLogo light withTagline={false} />
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">
                Get your business funder-ready. Pitch decks, financial models and management accounts — built for African founders.
              </p>
              <div className="mt-6 space-y-3 text-sm text-white/70">
                <a href="mailto:hello@clinq.africa" data-testid="footer-email"
                  className="group inline-flex items-center gap-2 hover:text-[#7BD3CE]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#7BD3CE]" aria-hidden="true">
                    <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                  </svg>
                  <span className="underline-offset-4 group-hover:underline">hello@clinq.africa</span>
                </a>
                <div className="flex items-start gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-[#7BD3CE]" aria-hidden="true">
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span className="text-white/60">Nairobi · Lagos · Cape Town</span>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2">
                <a href="https://x.com/clinqafrica" target="_blank" rel="noreferrer noopener"
                  aria-label="Clinq on X / Twitter" data-testid="footer-twitter"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#7BD3CE]/60 hover:text-[#7BD3CE]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                  </svg>
                </a>
                <a href="https://www.instagram.com/clinq.africa" target="_blank" rel="noreferrer noopener"
                  aria-label="Clinq on Instagram" data-testid="footer-instagram"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#7BD3CE]/60 hover:text-[#7BD3CE]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/clinq-africa/" target="_blank" rel="noreferrer noopener"
                  aria-label="Clinq on LinkedIn" data-testid="footer-linkedin"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#7BD3CE]/60 hover:text-[#7BD3CE]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                    <rect x="2" y="9" width="4" height="12"></rect>
                    <circle cx="4" cy="4" r="2"></circle>
                  </svg>
                </a>
              </div>
            </div>

            {/* Platform column */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">Build</p>
              <ul className="mt-4 space-y-2.5 text-sm text-white/70">
                <li><Link to="/build/start?type=pitch" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-pitch">Pitch Deck</Link></li>
                <li><Link to="/build/start?type=model" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-model">Financial Model</Link></li>
                <li><Link to="/build/start?type=accounts" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-accounts">Management Accounts</Link></li>
              </ul>
            </div>

            {/* Account column */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">Account</p>
              <ul className="mt-4 space-y-2.5 text-sm text-white/70">
                <li><Link to="/login" data-testid="footer-link-login" className="transition-colors hover:text-[#7BD3CE]">Log in</Link></li>
                <li><Link to="/signup" data-testid="footer-link-signup" className="transition-colors hover:text-[#7BD3CE]">Sign up</Link></li>
                <li><a href="https://clinq.africa" target="_blank" rel="noreferrer noopener" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-main">Back to clinq.africa</a></li>
              </ul>
            </div>

            {/* Legal column */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">Legal</p>
              <ul className="mt-4 space-y-2.5 text-sm text-white/70">
                <li><a href="https://clinq.africa/privacy" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-privacy">Privacy policy</a></li>
                <li><a href="https://clinq.africa/terms" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-terms">Terms of service</a></li>
                <li><a href="https://clinq.africa/nda" className="transition-colors hover:text-[#7BD3CE]" data-testid="footer-link-nda">NDA &amp; Non-circumvent</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 sm:px-8 py-5 text-[11px] text-white/40 sm:flex-row sm:items-center sm:justify-between">
            <p data-testid="footer-copyright">© 2026 Clinq. All rights reserved.</p>
            <p>Clinq is a technology platform. We do not provide investment, legal, or tax advice and are not a broker-dealer.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Metric = ({ value, label }) => (
  <div className="border-l-2 pl-4" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
    <div className="font-display font-extrabold text-3xl text-white tracking-tight">{value}</div>
    <div className="text-[10px] uppercase tracking-widest font-mono-num mt-1" style={{ color: C.teal }}>{label}</div>
  </div>
);
